"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.readingPageSchema = exports.readingSummarySchema = exports.readingListQuerySchema = void 0;
/**
 * `RPM-004` — the shape `GET /api/rpm/v1/readings` accepts and returns (`J-260`).
 *
 * THE UNWORKED QUEUE IS A QUERY, NOT A TABLE. The block's Postconditions row says so: "readings with
 * a null `rpm_alert_id` within the retention window". `worked` is the split, and it is a boolean
 * because the state is one — a reading either points at a workup or it does not.
 *
 * NO PARAMETER IS AN ATTRIBUTION FIELD. `packages/schema/src/attribution.test.ts:255` walks the
 * generated contract's QUERY PARAMETERS as well as its bodies, so a `createdBefore`/`createdAfter`
 * cursor fails the gate by name. The cursor is opaque for that reason and for a better one: a caller
 * that can express a page boundary in domain terms can express one the server did not intend.
 *
 * NO RESIDENT IDENTIFIER IN THE QUERY. `.claude/rules/phi.md` rule 2 keeps a resident's name, birth
 * date and id out of paths and query strings, so this route filters by BUILDING and never by person.
 * That is also why its `config.phi` carries no `patientParam`: the query names no resident, so the
 * audit row it writes names none either.
 */
const zod_1 = require("zod");
const rpm_enums_js_1 = require("./db/rpm-enums.js");
/** The mirror's identifier width. `carease.facilities.id` is varchar(20), and no FK reaches it. */
const MIRROR_ID = zod_1.z.string().min(1).max(20);
/**
 * The query.
 *
 * `facilityId` IS REQUIRED, and that is the scoping rather than a convenience. `RPM-004` acceptance
 * row 4 refuses the query for a building the caller has no assignment over, and a route that
 * defaulted to "every building you can reach" would have no single building to refuse.
 */
exports.readingListQuerySchema = zod_1.z
    .strictObject({
    facilityId: MIRROR_ID,
    /** False is the unworked queue: readings whose `rpm_alert_id` is null. */
    worked: zod_1.z.stringbool().default(false),
    limit: zod_1.z.coerce.number().int().min(1).max(200).default(50),
    /** Opaque. The server issues it and the server reads it; it is not a date and not an id. */
    cursor: zod_1.z.string().min(1).max(200).optional(),
    /**
     * A facility-local date range, both ends inclusive (`RPM-030`, `J-278`). The days are the
     * BUILDING's, computed in its operative zone when asked, and a reading belongs to the day its
     * `taken_at` falls in. "Any endpoint that takes a day takes a facility and a facility-local date
     * range, never a bare date": `facilityId` above is required, so a date without it is `400`.
     */
    fromLocalDate: zod_1.z.iso.date().optional(),
    toLocalDate: zod_1.z.iso.date().optional(),
})
    .refine((q) => (q.fromLocalDate === undefined) === (q.toLocalDate === undefined), {
    message: "A day range needs both fromLocalDate and toLocalDate.",
    path: ["toLocalDate"],
})
    .refine((q) => q.fromLocalDate === undefined ||
    q.toLocalDate === undefined ||
    q.fromLocalDate <= q.toLocalDate, { message: "fromLocalDate is after toLocalDate.", path: ["fromLocalDate"] })
    .meta({ description: "One building's readings, split by whether a workup has been opened" });
/**
 * One row of the worklist.
 *
 * EVERY PROVENANCE FACT THE ROW HOLDS TRAVELS, AND NONE IS OPTIONAL (`RPM-007`, `J-263`). The
 * enums are the schema's own, so a value the database can hold is a value this contract can carry
 * and a member added to one moves the other in the same commit.
 *
 * `rpmAlertId` TRAVELS, AND IT IS THE POINT. `RPM-004`'s whole subject is that a reading is complete
 * with it null; a client that could not see the column would have to infer the split from which
 * query it happened to run.
 *
 * THE RESIDENT IS NAMED, AND THAT IS NOT A PERSON READ (`J-475`). This comment used to say naming
 * her "would mean reading `people`, which `findPeopleReadsInHandlers` refuses". That was wrong and
 * it misled: a resident's name is `carease.patients.name` in the read-only mirror, which
 * `residents.repo.ts:55` already selects for the roster, and `PERSON_TABLES`
 * (`packages/schema/src/people-joins.ts:80`) is `people` and `person_tenant_affiliations` — the
 * mirror is neither. The project owner found the consequence on screen: a worklist column headed
 * Resident showing a mirror code. `RPM-004`'s Keyboard row expects the opposite — opening a row
 * "announces the resident and facility".
 *
 * STILL NO BIRTH DATE, NO BED AND NEVER AN MRN. The roster is `RPM-097` (`J-214`) and carries those;
 * a queue needs to say who the reading belongs to and nothing more. `RPM-097` puts the MRN on no
 * screen at all and `scripts/no-mrn-as-key.mjs` fails the gate over it.
 *
 * `careasePatientId` STAYS. It is what the reading stored and what a later read joins on; the name
 * is what a nurse reads. Dropping the id would make the row unjoinable to anything.
 */
exports.readingSummarySchema = zod_1.z
    .object({
    vitalReadingId: zod_1.z.uuid(),
    facilityId: MIRROR_ID,
    careasePatientId: MIRROR_ID,
    /**
     * The resident, as the mirror holds her name.
     *
     * NULLABLE, because the join is a LEFT one: a reading names a `carease_patient_id` with no
     * foreign key behind it (`CR-23`), so a mirror row that has gone leaves the reading standing and
     * the name absent. A surface shows the id in that case and says why, rather than showing a blank
     * where a person should be.
     */
    residentName: zod_1.z.string().nullable(),
    vitalCode: zod_1.z.enum(rpm_enums_js_1.vitalCode.enumValues),
    valueText: zod_1.z.string(),
    valueNumeric: zod_1.z.string().nullable(),
    valueUnit: zod_1.z.string().nullable(),
    takenAtLocalDateText: zod_1.z.string(),
    takenAtLocalTimeText: zod_1.z.string(),
    plausibilityStatus: zod_1.z.enum(["PLAUSIBLE", "IMPLAUSIBLE"]),
    // ---- `RPM-007`: the provenance facts, and none of them is optional (`J-263`) ----
    //
    // The block's API row: "Provenance fields appear in every read contract for a reading or alert;
    // they are never optional in the response type." Optional would mean a client could not tell
    // "this was not resolved" from "the server did not say", and the whole requirement is that a
    // reader can state how each fact was arrived at without re-running the resolution.
    //
    // FOUR OF THE FIVE ARE ON THE READING AND ARE ALL `NOT NULL` IN THE SCHEMA. The fifth — where
    // the resident's location came from — is `rpm_alerts.location_source`, set when a workup opens
    // (`RPM-100`, `J-265`), so it reaches a client through the alert's contract and not this one.
    vitalTypeResolution: zod_1.z.enum(rpm_enums_js_1.vitalTypeResolution.enumValues),
    unitResolution: zod_1.z.enum(rpm_enums_js_1.unitResolution.enumValues),
    timestampResolution: zod_1.z.enum(rpm_enums_js_1.timestampResolution.enumValues),
    /** Null while nothing has worked it up. Never inferred from which query returned the row. */
    rpmAlertId: zod_1.z.uuid().nullable(),
    sourceSystemId: zod_1.z.uuid(),
})
    .meta({ id: "ReadingSummary", description: "One recorded reading as a worklist shows it" });
/**
 * One page.
 *
 * `nextCursor` is null on the last page. There is no total: counting the queue is a second query
 * whose answer is stale the moment it is returned, and `RPM-004`'s Keyboard row asks for row-count
 * changes to be announced politely rather than for a number to be pinned in a header.
 */
exports.readingPageSchema = zod_1.z
    .object({
    items: zod_1.z.array(exports.readingSummarySchema),
    nextCursor: zod_1.z.string().nullable(),
    /**
     * The day range asked for, and the zone its boundaries were computed in — the header `RPM-030`'s
     * Operator surface asks a day-bucketed answer to state. Null when no range was asked.
     */
    dayRange: zod_1.z
        .object({ fromLocalDate: zod_1.z.string(), toLocalDate: zod_1.z.string(), zone: zod_1.z.string() })
        .nullable(),
})
    .meta({ id: "ReadingPage", description: "One page of a building's readings" });
//# sourceMappingURL=rpm-readings.js.map